/* Divine Medicinals — Wish List account sync
   ------------------------------------------------------------------
   Talks to the store's own App Proxy (same-origin, so Shopify signs each
   request and tells the backend who is logged in).

     GET  <proxy>/list        -> { customer: "<id>"|null, doc: <document>|null }
     POST <proxy>/save        -> { customer, doc }   body: { doc }

   The SERVER is the source of truth for who is signed in. We deliberately do
   not depend on Liquid's `customer` object, which has open reports of coming
   back blank under Shopify's new customer accounts — the kind this store uses.

   Everything degrades quietly: no proxy configured, backend down, offline, or
   a signed-out shopper all fall back to the browser-only behaviour, which is
   the full feature minus cross-device.
   ------------------------------------------------------------------ */
(function () {
  'use strict';

  if (!window.DMWish) return;

  var W = window.DMWish;
  var CFG = window.DMWishlistConfig || {};
  var PROXY = (CFG.proxyBase || '').replace(/\/+$/, '');
  if (!PROXY) return;                       // backend not set up yet — stay local

  var PUSH_DELAY = 1200;                    // coalesce a burst of edits into one write
  var MAX_BACKOFF = 60000;

  // Shopify can report a signed-in shopper as signed OUT on a page load. Taking
  // that first answer as final means every list saved on that page is lost, so
  // identity gets re-checked rather than latched.
  var IDENTITY_RETRIES = [2000, 5000, 12000, 30000, 60000];
  var identityTries = 0, identityTimer = null;

  var pushTimer = null;
  var inFlight = false;
  var dirty = false;
  var backoff = 1000;
  var lastPushed = null;                    // serialised doc we last sent, to skip no-ops

  function url(path) {
    return PROXY + path;
  }

  function req(path, opts) {
    return fetch(url(path), Object.assign({
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
    }, opts || {})).then(function (r) {
      if (!r.ok) {
        var err = new Error('sync http ' + r.status);
        err.status = r.status;
        throw err;
      }
      return r.json();
    });
  }

  function setStatus(s, detail) {
    document.documentElement.setAttribute('data-wl-sync', s);
    document.dispatchEvent(new CustomEvent('dm:wishlist:sync', {
      detail: Object.assign({ status: s }, detail || {})
    }));
  }

  /* ---------------- pull + merge on load ---------------- */

  function start() {
    setStatus('checking');
    req('/list').then(function (res) {
      var id = res && res.customer ? String(res.customer) : null;

      if (!id) {
        // signed out, or not recognised YET: keep the guest bucket and ask again
        W.setCustomer(null);
        W.sync();
        setStatus('guest');
        recheckIdentity();
        return;
      }
      identityTries = 0;
      clearTimeout(identityTimer);

      // point storage at this customer's bucket, then fold in anything they
      // saved as a guest in this browser before signing in
      W.setCustomer(id);
      var absorbed = W.absorbGuest();

      var local = W.read();
      var merged = res.doc ? W.mergeDocs(local, res.doc) : local;
      W.adopt(merged, 'sync');
      W.sync();

      // push back if the merge (or an absorbed guest list) added anything the
      // server does not have yet
      var out = JSON.stringify(W.forServer(merged));
      if (!res.doc || absorbed || out !== JSON.stringify(W.forServer(res.doc))) {
        schedulePush(0);
      } else {
        lastPushed = out;
      }
      setStatus('synced', { customer: id });
    }).catch(function (e) {
      // proxy missing, offline, or a backend error — local mode still works
      W.setCustomer(null);
      setStatus('offline', { error: String(e && e.message || e) });
    });
  }

  /* ---------------- push on change ---------------- */

  function schedulePush(delay) {
    if (!W.getCustomer()) return;
    dirty = true;
    clearTimeout(pushTimer);
    pushTimer = setTimeout(push, delay === undefined ? PUSH_DELAY : delay);
  }

  function push() {
    if (!W.getCustomer()) return;
    if (inFlight) { dirty = true; return; }

    var doc = W.forServer();
    var body = JSON.stringify(doc);
    if (body === lastPushed) { dirty = false; return; }

    inFlight = true;
    dirty = false;
    setStatus('saving');

    req('/save', { method: 'POST', body: JSON.stringify({ doc: doc }) })
      .then(function (res) {
        inFlight = false;
        backoff = 1000;

        // the server may have merged in a write from another device
        if (res && res.doc) {
          var merged = W.mergeDocs(W.read(), res.doc);
          var mergedOut = JSON.stringify(W.forServer(merged));
          if (mergedOut !== JSON.stringify(W.forServer(W.read()))) {
            W.adopt(merged, 'sync');
            W.sync();
            dirty = true;              // our merge added something the server lacks
          }
          lastPushed = JSON.stringify(W.forServer(res.doc));
        } else {
          lastPushed = body;
        }

        setStatus('synced');
        if (dirty) schedulePush();
      })
      .catch(function (e) {
        inFlight = false;
        if (e && e.status === 401) {   // session ended mid-visit
          W.setCustomer(null);
          setStatus('guest');
          return;
        }
        setStatus('offline', { error: String(e && e.message || e) });
        backoff = Math.min(MAX_BACKOFF, backoff * 2);
        schedulePush(backoff);
      });
  }

  /** Ask again whether anyone is signed in, backing off, a handful of times. */
  function recheckIdentity() {
    if (identityTries >= IDENTITY_RETRIES.length) return;
    var wait = IDENTITY_RETRIES[identityTries++];
    clearTimeout(identityTimer);
    identityTimer = setTimeout(start, wait);
  }

  document.addEventListener('dm:wishlist:change', function (e) {
    // changes we just adopted from the server must not bounce straight back
    if (e.detail && e.detail.origin === 'sync') return;
    // saving while we believe nobody is signed in is when a stale "guest"
    // verdict loses the list: re-check, and absorbGuest folds it in on success
    if (!W.getCustomer()) { identityTries = 0; start(); return; }
    schedulePush();
  });

  // last chance to save a pending edit when the tab goes away
  window.addEventListener('pagehide', flush);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') flush();
  });

  function flush() {
    if (!dirty || !W.getCustomer() || inFlight) return;
    clearTimeout(pushTimer);
    var body = JSON.stringify({ doc: W.forServer() });
    // sendBeacon survives the page unloading; the proxy signs it the same way
    if (navigator.sendBeacon) {
      try {
        navigator.sendBeacon(url('/save'), new Blob([body], { type: 'application/json' }));
        dirty = false;
        return;
      } catch (e) { /* fall through */ }
    }
    try {
      fetch(url('/save'), {
        method: 'POST', body: body, keepalive: true,
        credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }
      });
      dirty = false;
    } catch (e) {}
  }

  window.DMWishSync = {
    push: function () { schedulePush(0); },
    refresh: start,
    status: function () { return document.documentElement.getAttribute('data-wl-sync'); }
  };

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible' && !W.getCustomer()) { identityTries = 0; start(); }
  });

  if (document.readyState !== 'loading') start();
  else document.addEventListener('DOMContentLoaded', start);
})();
